import React, { useState, useEffect } from 'react';
import { Grid, CircularProgress, Tabs, Tab, Button } from '@mui/material';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import PDFViewer from 'src/components/pdf-viewer/PDFViewer';
import useAttachmentOcrResults, {
  OCRResult,
} from 'src/api/core/inbound-emails/useAttachmentOcrResults';
import { EmailAttachment } from './email-viewer-types';

interface OCRResultsTabProps {
  inboundEmailId?: string;
  attachments?: EmailAttachment[];
}

// Component to render JSON in an expandable, interactive way
interface JsonObject {
  [key: string]: JsonValue;
}
type JsonArray = JsonValue[];
type JsonValue =
  | string
  | number
  | boolean
  | null
  | undefined
  | JsonObject
  | JsonArray;

const JsonViewer = ({
  data,
  level = 0,
}: {
  data: JsonValue;
  level?: number;
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  if (data === null) return <span style={{ color: '#999' }}>null</span>;
  if (data === undefined)
    return <span style={{ color: '#999' }}>undefined</span>;

  if (typeof data !== 'object') {
    // Handle primitive values
    if (typeof data === 'string') {
      return <span style={{ color: '#009688' }}>&quot;{data}&quot;</span>;
    }
    if (typeof data === 'number') {
      return <span style={{ color: '#1976d2' }}>{data}</span>;
    }
    if (typeof data === 'boolean') {
      return (
        <span style={{ color: '#ff9800' }}>{data ? 'true' : 'false'}</span>
      );
    }
    return <span>{String(data)}</span>;
  }

  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span>[ ]</span>;
    }

    return (
      <div style={{ paddingLeft: level > 0 ? 20 : 0 }}>
        <span
          onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
          style={{ cursor: 'pointer', userSelect: 'none' }}
        >
          {expanded.root ? '▼' : '▶'} Array [{data.length}]
        </span>

        {expanded.root && (
          <div style={{ paddingLeft: 20 }}>
            {data.map((item, i) => (
              <div key={i}>
                <span style={{ color: '#555', marginRight: 8 }}>[{i}]:</span>
                <JsonViewer data={item} level={level + 1} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Object
  const keys = Object.keys(data);
  if (keys.length === 0) {
    return <span>{}</span>;
  }

  return (
    <div style={{ paddingLeft: level > 0 ? 20 : 0 }}>
      <span
        onClick={() => setExpanded((prev) => ({ ...prev, root: !prev.root }))}
        style={{ cursor: 'pointer', userSelect: 'none' }}
      >
        {expanded.root ? '▼' : '▶'} Object{' '}
        {`{${keys.length} ${keys.length === 1 ? 'key' : 'keys'}}`}
      </span>

      {expanded.root && (
        <div style={{ paddingLeft: 20 }}>
          {keys.map((key) => (
            <div key={key}>
              <span
                style={{ color: '#673ab7', fontWeight: 'bold', marginRight: 8 }}
              >
                {key}:
              </span>
              <JsonViewer data={data[key]} level={level + 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const OCRResultsTab: React.FC<OCRResultsTabProps> = ({
  inboundEmailId,
  attachments,
}) => {
  const [selectedAttachmentForOCR, setSelectedAttachmentForOCR] = useState<
    string | null
  >(null);
  const [activeTab, setActiveTab] = useState(0);
  const hasAttachments = !!attachments?.length;

  const {
    data: ocrData,
    isLoading: isLoadingOCR,
    error: ocrError,
  } = useAttachmentOcrResults(
    inboundEmailId || '',
    selectedAttachmentForOCR || '',
    !!(selectedAttachmentForOCR && inboundEmailId),
  );

  // Debug logging
  useEffect(() => {
    if (ocrData) {
      console.log('OCR Data received:', ocrData);
      console.log('OCR Results array:', ocrData?.ocrResults);
    }
    if (ocrError) {
      console.error('OCR Error:', ocrError);
    }
  }, [ocrData, ocrError]);

  // Find the selected attachment object
  const selectedAttachment = attachments?.find(
    (attachment) => attachment.id === selectedAttachmentForOCR,
  );

  // Parse extractedText[0] as JSON if it exists
  const parseExtractedText = (result: OCRResult) => {
    if (!result?.extractedText || !result.extractedText.length) {
      return null;
    }

    try {
      return JSON.parse(result.extractedText[0]);
    } catch (e) {
      console.error('Failed to parse extractedText as JSON:', e);
      return null;
    }
  };

  if (!hasAttachments) {
    return (
      <MDBox
        display="flex"
        justifyContent="center"
        alignItems="center"
        p={4}
        border="1px solid"
        borderColor="grey.300"
        borderRadius="borderRadius"
      >
        <MDTypography
          variant="caption2"
          color="text.secondary"
          fontStyle="italic"
          sx={{
            opacity: 0.2,
            letterSpacing: 0.5,
          }}
        >
          No attachments found
        </MDTypography>
      </MDBox>
    );
  }

  const renderAttachmentPreview = () => {
    if (!selectedAttachment) return null;

    const isPDF =
      selectedAttachment.mimeType === 'application/pdf' ||
      selectedAttachment.fileName?.toLowerCase().endsWith('.pdf');
    const isImage = selectedAttachment.mimeType?.startsWith('image/');

    if (isPDF) {
      return (
        <MDBox mb={3}>
          <MDTypography variant="subtitle2" mb={2}>
            Document Preview
          </MDTypography>
          <MDBox
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
            overflow="hidden"
            height="500px"
          >
            <PDFViewer url={selectedAttachment.s3Url} />
          </MDBox>
        </MDBox>
      );
    }

    if (isImage) {
      return (
        <MDBox mb={3}>
          <MDTypography variant="subtitle2" mb={2}>
            Image Preview
          </MDTypography>
          <MDBox
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
            p={1}
            textAlign="center"
          >
            <MDBox
              component="img"
              src={selectedAttachment.s3Url}
              alt={selectedAttachment.fileName}
              sx={{
                maxWidth: '100%',
                maxHeight: '500px',
                objectFit: 'contain',
              }}
            />
          </MDBox>
        </MDBox>
      );
    }

    return (
      <MDBox mb={3}>
        <MDTypography variant="subtitle2" mb={2}>
          File Preview
        </MDTypography>
        <MDBox
          p={3}
          border="1px solid"
          borderColor="grey.300"
          borderRadius="borderRadius"
          textAlign="center"
        >
          <MDTypography variant="body2" color="text.secondary">
            Preview not available for file type:{' '}
            {selectedAttachment.mimeType || 'Unknown'}
          </MDTypography>
          <MDTypography
            component="a"
            href={selectedAttachment.s3Url}
            target="_blank"
            rel="noopener noreferrer"
            color="info"
            sx={{ display: 'block', mt: 2 }}
          >
            Download File
          </MDTypography>
        </MDBox>
      </MDBox>
    );
  };

  return (
    <MDBox p={2}>
      <MDBox mb={3}>
        <MDTypography variant="subtitle2" mb={2}>
          Select an attachment to view OCR results:
        </MDTypography>
        <Grid container spacing={2}>
          {attachments?.map((attachment) => (
            <Grid item xs={12} md={6} lg={4} key={attachment.id}>
              <MDBox
                p={2}
                border="1px solid"
                borderColor={
                  selectedAttachmentForOCR === attachment.id
                    ? 'info.main'
                    : 'grey.300'
                }
                borderRadius="borderRadius"
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: 'info.main',
                    backgroundColor: 'rgba(0, 127, 255, 0.05)',
                  },
                }}
                onClick={() => setSelectedAttachmentForOCR(attachment.id)}
              >
                <MDTypography variant="body2" fontWeight="medium">
                  {attachment.fileName}
                </MDTypography>
                <MDTypography variant="caption" color="text.secondary">
                  {(attachment.sizeBytes / 1024).toFixed(2)} KB •{' '}
                  {attachment.mimeType || 'Unknown type'}
                </MDTypography>
              </MDBox>
            </Grid>
          ))}
        </Grid>
      </MDBox>

      {selectedAttachmentForOCR && (
        <>
          {renderAttachmentPreview()}

          <MDBox
            mt={3}
            p={3}
            border="1px solid"
            borderColor="grey.300"
            borderRadius="borderRadius"
          >
            <MDTypography variant="subtitle2" mb={2}>
              OCR Results
            </MDTypography>

            {isLoadingOCR ? (
              <MDBox display="flex" justifyContent="center" my={3}>
                <CircularProgress size={40} />
              </MDBox>
            ) : (
              <>
                {ocrData?.ocrResults?.length ? (
                  ocrData.ocrResults.map((result) => {
                    const parsedExtractedText = parseExtractedText(result);

                    return (
                      <MDBox key={result.id} mb={3}>
                        <MDBox mb={2}>
                          <MDTypography variant="body2" fontWeight="medium">
                            Provider: {result.vendor} (Model:{' '}
                            {result.modelVersion})
                          </MDTypography>
                          <MDTypography
                            variant="caption"
                            color="text.secondary"
                          >
                            Status: {result.status} - Created:{' '}
                            {new Date(result.createdAt).toLocaleString()}
                          </MDTypography>
                        </MDBox>

                        {result.error ? (
                          <MDBox
                            p={2}
                            bgcolor="error.light"
                            borderRadius="borderRadius"
                            color="error.dark"
                          >
                            <MDTypography variant="body2">
                              {result.error}
                            </MDTypography>
                          </MDBox>
                        ) : (
                          <MDBox>
                            <Tabs
                              value={activeTab}
                              onChange={(_, newValue) => setActiveTab(newValue)}
                              sx={{
                                mb: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                              }}
                            >
                              <Tab label="Parsed JSON" />
                              <Tab label="Raw Results" />
                            </Tabs>

                            {activeTab === 0 && (
                              <MDBox
                                p={2}
                                bgcolor="grey.50"
                                borderRadius="borderRadius"
                                sx={{
                                  maxHeight: '600px',
                                  overflowY: 'auto',
                                  fontFamily: 'monospace',
                                  fontSize: '14px',
                                }}
                              >
                                <MDBox
                                  mb={2}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {parsedExtractedText
                                      ? 'Extracted Text JSON (Parsed)'
                                      : 'No Valid JSON Found'}
                                  </MDTypography>
                                  {parsedExtractedText && (
                                    <Button
                                      size="small"
                                      color="info"
                                      variant="outlined"
                                      onClick={() => {
                                        if (!result.extractedText) return;
                                        navigator.clipboard.writeText(
                                          result.extractedText[0],
                                        );
                                      }}
                                    >
                                      Copy JSON String
                                    </Button>
                                  )}
                                </MDBox>

                                {parsedExtractedText ? (
                                  <JsonViewer
                                    data={
                                      parsedExtractedText as unknown as JsonValue
                                    }
                                  />
                                ) : (
                                  <MDTypography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    No valid JSON found in extractedText[0] or
                                    extractedText is empty.
                                    {result.extractedText &&
                                      result.extractedText.length > 0 && (
                                        <MDBox mt={2}>
                                          <MDTypography
                                            variant="body2"
                                            component="div"
                                          >
                                            Raw content of extractedText[0]:
                                          </MDTypography>
                                          <MDBox
                                            mt={1}
                                            p={2}
                                            bgcolor="grey.100"
                                            borderRadius="4px"
                                            sx={{
                                              whiteSpace: 'pre-wrap',
                                              overflowWrap: 'break-word',
                                              maxHeight: '200px',
                                              overflowY: 'auto',
                                            }}
                                          >
                                            {result.extractedText[0]}
                                          </MDBox>
                                        </MDBox>
                                      )}
                                  </MDTypography>
                                )}
                              </MDBox>
                            )}

                            {activeTab === 1 && (
                              <MDBox
                                p={2}
                                bgcolor="grey.50"
                                borderRadius="borderRadius"
                                sx={{
                                  maxHeight: '600px',
                                  overflowY: 'auto',
                                  fontFamily: 'monospace',
                                  fontSize: '14px',
                                }}
                              >
                                <MDBox
                                  mb={2}
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <MDTypography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    Complete OCR Result
                                  </MDTypography>
                                  <Button
                                    size="small"
                                    color="info"
                                    variant="outlined"
                                    onClick={() => {
                                      const jsonStr = JSON.stringify(
                                        result,
                                        null,
                                        2,
                                      );
                                      navigator.clipboard.writeText(jsonStr);
                                    }}
                                  >
                                    Copy Full JSON
                                  </Button>
                                </MDBox>

                                <JsonViewer
                                  data={result as unknown as JsonValue}
                                />
                              </MDBox>
                            )}
                          </MDBox>
                        )}
                      </MDBox>
                    );
                  })
                ) : (
                  <MDTypography variant="body2" color="text.secondary">
                    No OCR results found for this attachment
                  </MDTypography>
                )}
              </>
            )}
          </MDBox>
        </>
      )}
    </MDBox>
  );
};

export default OCRResultsTab;
